import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, Edit2, Save } from "lucide-react";

const REGIMEN = [
  ["605", "Sueldos y Salarios"],
  ["612", "Persona Física con actividad empresarial"],
  ["616", "Sin obligaciones fiscales"],
  ["621", "Incorporación Fiscal"],
  ["626", "RESICO"],
  ["601", "General de Ley Personas Morales"],
  ["603", "Personas Morales con Fines no Lucrativos"],
];
const USOS = [["G03","G03 · Gastos en general"],["G01","G01 · Adquisición"],["S01","S01 · Sin efectos"],["P01","P01 · Por definir"]];

export default function Invoices() {
  const [tax, setTax] = useState({ needs_invoice: false });
  const [invoices, setInvoices] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/invoices/tax-data").then(({ data }) => setTax(data || { needs_invoice: false }));
    api.get("/invoices/list").then(({ data }) => setInvoices(data || []));
  };
  useEffect(() => { load(); }, []);

  const saveTax = async () => {
    setSaving(true);
    try {
      await api.put("/invoices/tax-data", tax);
      toast.success("Datos fiscales guardados");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const downloadPdf = (id, folio, kind = "comprobante") => {
    const path = kind === "comprobante" ? `/invoices/${id}/pdf` : `/invoices/${id}/cfdi/${kind}`;
    const url = `${api.defaults.baseURL}${path}`;
    const token = localStorage.getItem("cia_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${kind}-${folio}.${kind === "xml" ? "xml" : "pdf"}`;
        a.click();
      })
      .catch((e) => toast.error("Error descargando: " + e.message));
  };

  const issueCfdi = async (id) => {
    try {
      const { data } = await api.post(`/invoices/${id}/issue-cfdi`);
      if (data.ok) toast.success("CFDI emitido: " + (data.uuid || data.cfdi_id));
      else toast.error("Error: " + (data.error || "ver logs"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">CIA · BILLING · FACTURAS</div>
        <h1 className="font-display text-4xl">Facturación</h1>
        <p className="font-mono-data text-sm text-[#5C5C5C] mt-1">
          Datos fiscales para tus comprobantes y lista de facturas.
        </p>
      </div>

      {/* Tax data card */}
      <div className="bg-white border border-[#0A0A0A] p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono-mark text-[11px]">TUS DATOS FISCALES</div>
          {!editing ? (
            <button onClick={() => setEditing(true)} className="font-mono-mark text-[10px] px-3 py-1.5 border border-[#0A0A0A] flex items-center gap-1" data-testid="tax-edit">
              <Edit2 className="w-3 h-3" /> EDITAR
            </button>
          ) : (
            <button onClick={saveTax} disabled={saving}
              className="font-mono-mark text-[10px] bg-[#0A0A0A] text-white px-3 py-1.5 flex items-center gap-1" data-testid="tax-save">
              <Save className="w-3 h-3" /> {saving ? "..." : "GUARDAR"}
            </button>
          )}
        </div>

        {editing ? (
          <div className="space-y-3">
            <label className="flex items-center gap-2 font-mono-data text-sm">
              <input type="checkbox" checked={!!tax.needs_invoice} onChange={(e) => setTax({ ...tax, needs_invoice: e.target.checked })} data-testid="tax-needs" />
              Sí, necesito factura por mis pagos
            </label>
            {tax.needs_invoice && (
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={tax.rfc || ""} onChange={(e) => setTax({ ...tax, rfc: e.target.value.toUpperCase() })} placeholder="RFC *" maxLength={13}
                  className="bg-[#F4F4F0] border border-[#0A0A0A] p-3 font-mono-data text-sm uppercase" data-testid="tax-rfc" />
                <input value={tax.razon_social || ""} onChange={(e) => setTax({ ...tax, razon_social: e.target.value })} placeholder="Razón social *"
                  className="bg-[#F4F4F0] border border-[#0A0A0A] p-3 font-mono-data text-sm" />
                <input value={tax.cp_fiscal || ""} onChange={(e) => setTax({ ...tax, cp_fiscal: e.target.value })} placeholder="C.P. fiscal *" maxLength={5}
                  className="bg-[#F4F4F0] border border-[#0A0A0A] p-3 font-mono-data text-sm" />
                <select value={tax.regimen_fiscal || "612"} onChange={(e) => setTax({ ...tax, regimen_fiscal: e.target.value })}
                  className="bg-[#F4F4F0] border border-[#0A0A0A] p-3 font-mono-data text-sm">
                  {REGIMEN.map(([k, v]) => <option key={k} value={k}>{k} · {v}</option>)}
                </select>
                <select value={tax.uso_cfdi || "G03"} onChange={(e) => setTax({ ...tax, uso_cfdi: e.target.value })}
                  className="bg-[#F4F4F0] border border-[#0A0A0A] p-3 font-mono-data text-sm">
                  {USOS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <input value={tax.fiscal_email || ""} onChange={(e) => setTax({ ...tax, fiscal_email: e.target.value })} placeholder="Email fiscal" type="email"
                  className="bg-[#F4F4F0] border border-[#0A0A0A] p-3 font-mono-data text-sm" />
              </div>
            )}
          </div>
        ) : (
          <div className="font-mono-data text-sm space-y-1">
            <div><b>¿Necesita factura?</b> {tax.needs_invoice ? "Sí" : "No"}</div>
            {tax.needs_invoice && (
              <>
                <div><b>RFC:</b> {tax.rfc}</div>
                <div><b>Razón social:</b> {tax.razon_social}</div>
                <div><b>C.P. fiscal:</b> {tax.cp_fiscal}</div>
                <div><b>Régimen:</b> {tax.regimen_fiscal}</div>
                <div><b>Uso CFDI:</b> {tax.uso_cfdi}</div>
                {tax.fiscal_email && <div><b>Email fiscal:</b> {tax.fiscal_email}</div>}
              </>
            )}
          </div>
        )}
      </div>

      {/* Invoices list */}
      <div className="bg-white border border-[#0A0A0A]">
        <div className="px-6 py-4 border-b border-[#0A0A0A] font-mono-mark text-[11px]">HISTORIAL DE COMPROBANTES</div>
        {invoices.length === 0 ? (
          <div className="p-8 text-center font-mono-data text-sm text-[#5C5C5C]" data-testid="invoices-empty">
            <FileText className="w-8 h-8 mx-auto mb-3 text-[#5C5C5C]/50" />
            Aún no tienes pagos registrados. Cuando pagues tu suscripción, aparecerán aquí.
          </div>
        ) : (
          <div className="divide-y divide-[#0A0A0A]/15">
            {invoices.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between gap-3 p-4" data-testid={`invoice-${inv.folio}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{inv.folio}</div>
                  <div className="font-mono-data text-sm truncate">{inv.concept}</div>
                  <div className="font-mono-data text-xs text-[#5C5C5C]">
                    {new Date(inv.issued_at).toLocaleDateString("es-MX")} ·
                    {inv.cfdi_status === "pending" ? " CFDI pendiente" : inv.cfdi_status === "issued" ? " CFDI emitido" : " sin factura"}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-xl">${inv.amount.toFixed(2)}</div>
                  <div className="font-mono-mark text-[9px] text-[#5C5C5C]">{(inv.currency || "USD").toUpperCase()}</div>
                </div>
                <div className="flex flex-wrap gap-1 items-center">
                  <button onClick={() => downloadPdf(inv.id, inv.folio, "comprobante")} title="Comprobante interno"
                    className="font-mono-mark text-[10px] px-3 py-2 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white flex items-center gap-1" data-testid={`invoice-pdf-${inv.folio}`}>
                    <Download className="w-3 h-3" /> COMPROB
                  </button>
                  {inv.cfdi_status === "issued" && (
                    <>
                      <button onClick={() => downloadPdf(inv.id, inv.folio, "pdf")}
                        className="font-mono-mark text-[10px] px-3 py-2 border-2 border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white flex items-center gap-1" data-testid={`cfdi-pdf-${inv.folio}`}>
                        CFDI PDF
                      </button>
                      <button onClick={() => downloadPdf(inv.id, inv.folio, "xml")}
                        className="font-mono-mark text-[10px] px-3 py-2 border border-[#16A34A] text-[#16A34A] flex items-center gap-1">
                        XML
                      </button>
                    </>
                  )}
                  {(inv.cfdi_status === "pending" || inv.cfdi_status === "error") && inv.customer?.needs_invoice && (
                    <button onClick={() => issueCfdi(inv.id)}
                      className="font-mono-mark text-[10px] bg-[#FFD600] border border-[#0A0A0A] px-3 py-2 hover-brutal flex items-center gap-1" data-testid={`cfdi-issue-${inv.folio}`}>
                      EMITIR CFDI
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
